import React, { useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { COLORS, FONT } from "../../constants";

const ACCEPTED_EXTS = ["pdf", "docx", "doc", "png", "jpeg", "jpg"];
const ACCEPT_ATTR   = ".pdf,.docx,.doc,.png,.jpeg,.jpg";

const getFileIcon = (file) => {
  const ext = file.name.split(".").pop().toLowerCase();
  if (ext === "pdf")                        return <PictureAsPdfOutlinedIcon sx={{ fontSize: 28, color: "#e53935" }} />;
  if (["png", "jpg", "jpeg"].includes(ext)) return <ImageOutlinedIcon       sx={{ fontSize: 28, color: "#7c4dff" }} />;
  return                                           <DescriptionOutlinedIcon  sx={{ fontSize: 28, color: "#1565c0" }} />;
};

const formatBytes = (bytes) => {
  if (bytes < 1024)             return `${bytes} B`;
  if (bytes < 1024 * 1024)      return `${(bytes / 1024).toFixed(1)} KB`;
  return                               `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUploadBox = ({ label, sublabel, required = false, multiple = false, files = [], onChange }) => {
  const inputRef    = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      return ACCEPTED_EXTS.includes(ext);
    });
    onChange(multiple ? [...files, ...valid] : valid.slice(0, 1));
  };

  const removeFile = (index) => onChange(files.filter((_, i) => i !== index));

  return (
    <Box>
      {label && (
        <Typography sx={{ fontSize: FONT.size.xs, fontWeight: 600, color: COLORS.darkText, mb: 0.5, letterSpacing: 0.5 }}>
          {label}
          {required && <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>*</Box>}
        </Typography>
      )}
      {sublabel && (
        <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.lightText, mb: 1 }}>
          {sublabel}
        </Typography>
      )}

      {/* Drop zone */}
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={(e)  => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={()  => setIsDragging(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }}
        sx={{
          border: `2px dashed ${isDragging ? COLORS.themeBlue : "#cccccc"}`,
          borderRadius: "4px",
          backgroundColor: isDragging ? "rgba(13,119,219,0.04)" : "#fafafa",
          py: 4,
          px: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          cursor: "pointer",
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": { borderColor: COLORS.themeBlue, backgroundColor: "rgba(13,119,219,0.02)" },
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: "#aaaaaa", mb: 1.5 }} />
        <Typography sx={{ fontSize: FONT.size.sm, color: COLORS.darkText, fontWeight: 500 }}>
          Click to select {multiple ? "files" : "a file"} to upload
        </Typography>
        <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.lightText, mt: 0.5 }}>
          or drag and drop here
        </Typography>
        <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.lightText, mt: 0.25 }}>
          PDF, DOCX, PNG, JPEG
        </Typography>
      </Box>

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={ACCEPT_ATTR}
        style={{ display: "none" }}
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />

      {/* File list */}
      {files.length > 0 && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          {files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                border: `1px solid #e0e0e0`,
                borderRadius: "4px",
                backgroundColor: COLORS.white,
              }}
            >
              {getFileIcon(file)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: FONT.size.xs, fontWeight: 600, color: COLORS.darkText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {file.name}
                </Typography>
                <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.lightText }}>
                  {formatBytes(file.size)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                sx={{ color: COLORS.lightText, "&:hover": { color: COLORS.themeRed } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FileUploadBox;
